// src/components/tools/operational-planner/OperationalPlannerTool.jsx
// Comprehensive Operational Planning Tool with Multiple Views
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye,
  Calendar, CalendarDays, CalendarRange, LayoutGrid, List, Rows3,
  ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Clock, Target,
  CheckCircle, Circle, PlayCircle, PauseCircle, XCircle, AlertCircle,
  TrendingUp, TrendingDown, Minus, Save, Loader2, X, Check, Flag,
  GripVertical, ArrowUpDown, Sparkles, BarChart3, FileText, Users,
  Building2, Folder, FolderOpen, Plus as PlusIcon, MessageSquare,
  ThumbsUp, ThumbsDown, RefreshCw, Maximize2, Minimize2
} from 'lucide-react';

// ============================================================
// CONSTANTS
// ============================================================

const ViewMode = {
  CARDS: 'cards',
  YEAR_PLANNER: 'year_planner'
};

const PlannerView = {
  MONTHLY_GRID: 'monthly_grid',
  MONTHLY_LIST: 'monthly_list',
  WEEKLY_GRID: 'weekly_grid',
  WEEKLY_LIST: 'weekly_list'
};

const ItemStatus = {
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ON_HOLD: 'on_hold',
  CANCELLED: 'cancelled'
};

const CompletionResult = {
  SUCCESSFUL: 'successful',
  PARTIALLY_SUCCESSFUL: 'partially_successful',
  NOT_SUCCESSFUL: 'not_successful'
};

const Priority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const formatDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateShort = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
};

const getWeeksInMonth = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const weeks = [];
  
  let currentDate = new Date(firstDay);
  currentDate.setDate(currentDate.getDate() - currentDate.getDay()); // Start from Sunday
  
  while (currentDate <= lastDay || weeks.length < 6) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(week);
    if (currentDate > lastDay && week[0].getMonth() !== month) break;
  }
  
  return weeks;
};

const getWeekNumber = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const getStatusConfig = (status) => {
  const configs = {
    [ItemStatus.PLANNED]: { label: 'Planned', color: 'gray', icon: Circle, bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
    [ItemStatus.IN_PROGRESS]: { label: 'In Progress', color: 'blue', icon: PlayCircle, bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
    [ItemStatus.COMPLETED]: { label: 'Completed', color: 'emerald', icon: CheckCircle, bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    [ItemStatus.ON_HOLD]: { label: 'On Hold', color: 'amber', icon: PauseCircle, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
    [ItemStatus.CANCELLED]: { label: 'Cancelled', color: 'red', icon: XCircle, bgColor: 'bg-red-100', textColor: 'text-red-700' }
  };
  return configs[status] || configs[ItemStatus.PLANNED];
};

const getPriorityConfig = (priority) => {
  const configs = {
    [Priority.LOW]: { label: 'Low', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-600' },
    [Priority.MEDIUM]: { label: 'Medium', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-600' },
    [Priority.HIGH]: { label: 'High', color: 'amber', bgColor: 'bg-amber-100', textColor: 'text-amber-600' },
    [Priority.CRITICAL]: { label: 'Critical', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-600' }
  };
  return configs[priority] || configs[Priority.MEDIUM];
};

const getCompletionConfig = (result) => {
  const configs = {
    [CompletionResult.SUCCESSFUL]: { label: 'Successful', color: 'emerald', icon: ThumbsUp, bgColor: 'bg-emerald-100', textColor: 'text-emerald-700' },
    [CompletionResult.PARTIALLY_SUCCESSFUL]: { label: 'Partial', color: 'amber', icon: Minus, bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
    [CompletionResult.NOT_SUCCESSFUL]: { label: 'Not Successful', color: 'red', icon: ThumbsDown, bgColor: 'bg-red-100', textColor: 'text-red-700' }
  };
  return configs[result] || configs[CompletionResult.SUCCESSFUL];
};

const isDateInRange = (date, startDate, endDate) => {
  const d = new Date(date).setHours(0, 0, 0, 0);
  const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
  const end = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : null;
  
  if (start && end) return d >= start && d <= end;
  if (start) return d >= start;
  if (end) return d <= end;
  return false;
};

// ============================================================
// UI COMPONENTS
// ============================================================

const StatusBadge = ({ status, size = 'sm' }) => {
  const config = getStatusConfig(status);
  const Icon = config.icon;
  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${config.bgColor} ${config.textColor} ${sizeClasses}`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {config.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const config = getPriorityConfig(priority);
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.textColor}`}>
      {config.label}
    </span>
  );
};

const CompletionBadge = ({ result }) => {
  const config = getCompletionConfig(result);
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

const ProgressBar = ({ progress = 0, showLabel = true, size = 'md' }) => {
  const heights = { sm: 'h-1.5', md: 'h-2', lg: 'h-3' };
  return (
    <div className="flex items-center gap-2">
      <div className={`flex-1 bg-gray-200 rounded-full overflow-hidden ${heights[size]}`}>
        <div
          className={`h-full rounded-full transition-all ${
            progress >= 100 ? 'bg-emerald-500' : progress >= 50 ? 'bg-blue-500' : 'bg-amber-500'
          }`}
          style={{ width: `${Math.min(100, progress)}%` }}
        />
      </div>
      {showLabel && <span className="text-xs text-gray-500 w-10 text-right">{progress}%</span>}
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

// ============================================================
// PLAN FORM
// ============================================================

const PlanForm = ({ plan, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    year: new Date().getFullYear(),
    category: '',
    color: 'indigo',
    ...plan
  });

  const colors = ['indigo', 'blue', 'emerald', 'amber', 'rose', 'purple', 'cyan', 'orange'];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Plan name is required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {plan?.id ? 'Edit Plan' : 'Create Operational Plan'}
        </h1>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., 2024 Marketing Operations"
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            placeholder="Describe the purpose and scope of this plan..."
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={formData.year}
              onChange={(e) => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - 1 + i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              placeholder="e.g., Marketing"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Color Theme</label>
          <div className="flex gap-2">
            {colors.map(color => (
              <button
                key={color}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, color }))}
                className={`w-8 h-8 rounded-full bg-${color}-500 transition-transform ${
                  formData.color === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : ''
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {plan?.id ? 'Update' : 'Create'} Plan
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// ITEM FORM
// ============================================================

const ItemForm = ({ item, planId, onSave, onCancel, saving }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: ItemStatus.PLANNED,
    priority: Priority.MEDIUM,
    startDate: '',
    endDate: '',
    progress: 0,
    assignee: '',
    tags: [],
    completionResult: null,
    completionNotes: '',
    planId,
    ...item
  });

  const [newTag, setNewTag] = useState('');

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      alert('Title is required');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-xl">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {item?.id ? 'Edit Item' : 'Add Plan Item'}
        </h1>
      </div>

      <div className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Item Details
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Launch social media campaign"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              placeholder="Describe this item in detail..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              >
                {Object.values(ItemStatus).map(status => (
                  <option key={status} value={status}>{getStatusConfig(status).label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              >
                {Object.values(Priority).map(priority => (
                  <option key={priority} value={priority}>{getPriorityConfig(priority).label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
            <input
              type="text"
              value={formData.assignee}
              onChange={(e) => setFormData(prev => ({ ...prev, assignee: e.target.value }))}
              placeholder="Person responsible"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add tag..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        {/* Progress & Completion */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Progress & Completion
          </h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Progress: {formData.progress}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.progress}
              onChange={(e) => setFormData(prev => ({ ...prev, progress: parseInt(e.target.value) }))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {formData.status === ItemStatus.COMPLETED && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Completion Result</label>
                <select
                  value={formData.completionResult || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, completionResult: e.target.value || null }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                >
                  <option value="">Select result...</option>
                  {Object.values(CompletionResult).map(result => (
                    <option key={result} value={result}>{getCompletionConfig(result).label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Completion Notes</label>
                <textarea
                  value={formData.completionNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, completionNotes: e.target.value }))}
                  rows={3}
                  placeholder="Notes about completion outcome..."
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
                />
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {item?.id ? 'Update' : 'Add'} Item
          </button>
        </div>
      </div>
    </form>
  );
};

// ============================================================
// PROGRESS UPDATE MODAL
// ============================================================

const ProgressUpdateModal = ({ item, onSave, onClose, saving }) => {
  const [progress, setProgress] = useState(item?.progress || 0);
  const [status, setStatus] = useState(item?.status || ItemStatus.IN_PROGRESS);
  const [note, setNote] = useState('');
  const [completionResult, setCompletionResult] = useState(item?.completionResult || '');

  const handleSave = () => {
    const update = {
      progress,
      status,
      completionResult: status === ItemStatus.COMPLETED ? completionResult : null,
      progressUpdates: [
        ...(item?.progressUpdates || []),
        {
          id: Date.now(),
          progress,
          status,
          note,
          completionResult: status === ItemStatus.COMPLETED ? completionResult : null,
          updatedAt: new Date().toISOString()
        }
      ]
    };
    onSave(item.id, update);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Update Progress</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <p className="font-medium text-gray-900 mb-1">{item?.title}</p>
            <p className="text-sm text-gray-500">{item?.description}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Progress: {progress}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={(e) => setProgress(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            >
              {Object.values(ItemStatus).map(s => (
                <option key={s} value={s}>{getStatusConfig(s).label}</option>
              ))}
            </select>
          </div>

          {status === ItemStatus.COMPLETED && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Completion Result</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.values(CompletionResult).map(result => {
                  const config = getCompletionConfig(result);
                  const Icon = config.icon;
                  return (
                    <button
                      key={result}
                      type="button"
                      onClick={() => setCompletionResult(result)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                        completionResult === result
                          ? `border-${config.color}-500 ${config.bgColor}`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${config.textColor}`} />
                      <span className="text-xs font-medium">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Update Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Add a note about this progress update..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save Update
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// ITEM CARD (Horizontal Sortable)
// ============================================================

const ItemCard = ({ item, onEdit, onDelete, onUpdateProgress, isDragging }) => {
  const statusConfig = getStatusConfig(item.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={`bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all ${
        isDragging ? 'shadow-lg scale-105 rotate-1' : ''
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div className="pt-1 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-400">
          <GripVertical className="w-5 h-5" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">{item.title}</h3>
              {item.description && (
                <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{item.description}</p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onUpdateProgress(item)}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                title="Update Progress"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => onEdit(item)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => { if(window.confirm('Delete item?')) onDelete(item.id); }}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Meta Row */}
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <StatusBadge status={item.status} />
            <PriorityBadge priority={item.priority} />
            {item.completionResult && <CompletionBadge result={item.completionResult} />}
            {(item.startDate || item.endDate) && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDateShort(item.startDate)} - {formatDateShort(item.endDate)}
              </span>
            )}
          </div>

          {/* Progress */}
          <ProgressBar progress={item.progress || 0} />

          {/* Tags */}
          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item.tags.map(tag => (
                <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CARDS VIEW
// ============================================================

const CardsView = ({ items, onEdit, onDelete, onUpdateProgress, onReorder }) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [sortBy, setSortBy] = useState('priority');
  const [filterStatus, setFilterStatus] = useState('all');

  const filteredItems = useMemo(() => {
    let result = [...items];
    
    if (filterStatus !== 'all') {
      result = result.filter(item => item.status === filterStatus);
    }
    
    // Sort
    result.sort((a, b) => {
      if (sortBy === 'priority') {
        const order = { critical: 0, high: 1, medium: 2, low: 3 };
        return (order[a.priority] || 2) - (order[b.priority] || 2);
      }
      if (sortBy === 'startDate') {
        return new Date(a.startDate || 0) - new Date(b.startDate || 0);
      }
      if (sortBy === 'progress') {
        return (b.progress || 0) - (a.progress || 0);
      }
      return 0;
    });
    
    return result;
  }, [items, filterStatus, sortBy]);

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (draggedItem && draggedItem.id !== targetItem.id) {
      const newItems = [...items];
      const dragIndex = newItems.findIndex(i => i.id === draggedItem.id);
      const dropIndex = newItems.findIndex(i => i.id === targetItem.id);
      
      newItems.splice(dragIndex, 1);
      newItems.splice(dropIndex, 0, draggedItem);
      
      onReorder(newItems);
    }
    setDraggedItem(null);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="all">All Status</option>
            {Object.values(ItemStatus).map(status => (
              <option key={status} value={status}>{getStatusConfig(status).label}</option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="priority">Sort by Priority</option>
            <option value="startDate">Sort by Start Date</option>
            <option value="progress">Sort by Progress</option>
          </select>
        </div>
        <span className="text-sm text-gray-500">{filteredItems.length} items</span>
      </div>

      {/* Cards */}
      {filteredItems.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No items found"
          description="Add items to your plan or adjust filters"
        />
      ) : (
        <div className="space-y-3">
          {filteredItems.map(item => (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, item)}
            >
              <ItemCard
                item={item}
                onEdit={onEdit}
                onDelete={onDelete}
                onUpdateProgress={onUpdateProgress}
                isDragging={draggedItem?.id === item.id}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// YEAR PLANNER - MONTHLY GRID VIEW
// ============================================================

const MonthlyGridView = ({ items, year, onItemClick }) => {
  const getItemsForMonth = (month) => {
    return items.filter(item => {
      if (!item.startDate && !item.endDate) return false;
      
      const start = item.startDate ? new Date(item.startDate) : null;
      const end = item.endDate ? new Date(item.endDate) : null;
      
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);
      
      if (start && end) {
        return start <= monthEnd && end >= monthStart;
      }
      if (start) {
        return start.getFullYear() === year && start.getMonth() === month;
      }
      if (end) {
        return end.getFullYear() === year && end.getMonth() === month;
      }
      return false;
    });
  };

  return (
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {MONTHS.map((month, index) => {
        const monthItems = getItemsForMonth(index);
        const completedCount = monthItems.filter(i => i.status === ItemStatus.COMPLETED).length;
        
        return (
          <div key={month} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 text-sm">{month}</h3>
              <p className="text-xs text-gray-500">{monthItems.length} items</p>
            </div>
            <div className="p-2 space-y-1 max-h-40 overflow-y-auto">
              {monthItems.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No items</p>
              ) : (
                monthItems.slice(0, 5).map(item => {
                  const statusConfig = getStatusConfig(item.status);
                  return (
                    <button
                      key={item.id}
                      onClick={() => onItemClick(item)}
                      className={`w-full text-left px-2 py-1.5 rounded-lg text-xs truncate ${statusConfig.bgColor} ${statusConfig.textColor} hover:opacity-80 transition-opacity`}
                    >
                      {item.title}
                    </button>
                  );
                })
              )}
              {monthItems.length > 5 && (
                <p className="text-xs text-gray-400 text-center">+{monthItems.length - 5} more</p>
              )}
            </div>
            {monthItems.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{completedCount} done</span>
                  <span className="text-gray-400">{Math.round((completedCount / monthItems.length) * 100)}%</span>
                </div>
                <ProgressBar progress={Math.round((completedCount / monthItems.length) * 100)} showLabel={false} size="sm" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// YEAR PLANNER - MONTHLY LIST VIEW
// ============================================================

const MonthlyListView = ({ items, year, onItemClick, onUpdateProgress }) => {
  const [expandedMonth, setExpandedMonth] = useState(new Date().getMonth());

  const getItemsForMonth = (month) => {
    return items.filter(item => {
      if (!item.startDate && !item.endDate) return false;
      
      const start = item.startDate ? new Date(item.startDate) : null;
      const end = item.endDate ? new Date(item.endDate) : null;
      
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0);
      
      if (start && end) {
        return start <= monthEnd && end >= monthStart;
      }
      if (start) {
        return start.getFullYear() === year && start.getMonth() === month;
      }
      return false;
    });
  };

  return (
    <div className="space-y-2">
      {MONTHS.map((month, index) => {
        const monthItems = getItemsForMonth(index);
        const isExpanded = expandedMonth === index;
        const completedCount = monthItems.filter(i => i.status === ItemStatus.COMPLETED).length;

        return (
          <div key={month} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <button
              onClick={() => setExpandedMonth(isExpanded ? -1 : index)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                )}
                <span className="font-medium text-gray-900">{month}</span>
                <span className="text-sm text-gray-500">({monthItems.length} items)</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">
                  {completedCount}/{monthItems.length} completed
                </span>
                <div className="w-24">
                  <ProgressBar
                    progress={monthItems.length ? Math.round((completedCount / monthItems.length) * 100) : 0}
                    showLabel={false}
                    size="sm"
                  />
                </div>
              </div>
            </button>

            {isExpanded && monthItems.length > 0 && (
              <div className="border-t border-gray-100 divide-y divide-gray-50">
                {monthItems.map(item => {
                  const statusConfig = getStatusConfig(item.status);
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-4 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <StatusIcon className={`w-5 h-5 text-${statusConfig.color}-500`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{item.title}</p>
                          <p className="text-sm text-gray-500">
                            {formatDateShort(item.startDate)} - {formatDateShort(item.endDate)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20">
                          <ProgressBar progress={item.progress || 0} size="sm" />
                        </div>
                        <PriorityBadge priority={item.priority} />
                        {item.completionResult && <CompletionBadge result={item.completionResult} />}
                        <button
                          onClick={() => onUpdateProgress(item)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onItemClick(item)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {isExpanded && monthItems.length === 0 && (
              <div className="p-8 text-center text-gray-400 text-sm border-t border-gray-100">
                No items scheduled for this month
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// YEAR PLANNER - WEEKLY GRID VIEW
// ============================================================

const WeeklyGridView = ({ items, year, selectedMonth, onItemClick, onMonthChange }) => {
  const weeks = getWeeksInMonth(year, selectedMonth);

  const getItemsForWeek = (weekDays) => {
    const weekStart = weekDays[0];
    const weekEnd = weekDays[6];
    
    return items.filter(item => {
      if (!item.startDate && !item.endDate) return false;
      
      const start = item.startDate ? new Date(item.startDate) : null;
      const end = item.endDate ? new Date(item.endDate) : null;
      
      if (start && end) {
        return start <= weekEnd && end >= weekStart;
      }
      if (start) {
        return start >= weekStart && start <= weekEnd;
      }
      return false;
    });
  };

  return (
    <div className="space-y-4">
      {/* Month Selector */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onMonthChange(selectedMonth === 0 ? 11 : selectedMonth - 1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-semibold text-gray-900">
          {MONTHS[selectedMonth]} {year}
        </h3>
        <button
          onClick={() => onMonthChange(selectedMonth === 11 ? 0 : selectedMonth + 1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100">
          {DAYS_SHORT.map(day => (
            <div key={day} className="p-2 text-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Weeks */}
        {weeks.map((week, weekIndex) => {
          const weekItems = getItemsForWeek(week);
          
          return (
            <div key={weekIndex} className="border-b border-gray-100 last:border-b-0">
              {/* Days Row */}
              <div className="grid grid-cols-7">
                {week.map((day, dayIndex) => {
                  const isCurrentMonth = day.getMonth() === selectedMonth;
                  const isToday = day.toDateString() === new Date().toDateString();
                  
                  return (
                    <div
                      key={dayIndex}
                      className={`p-2 min-h-[60px] border-r border-gray-50 last:border-r-0 ${
                        !isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                      }`}
                    >
                      <span className={`text-xs font-medium ${
                        isToday ? 'w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center' : ''
                      }`}>
                        {day.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Items Row */}
              {weekItems.length > 0 && (
                <div className="px-2 py-1 bg-gray-50/50 space-y-1">
                  {weekItems.slice(0, 3).map(item => {
                    const statusConfig = getStatusConfig(item.status);
                    return (
                      <button
                        key={item.id}
                        onClick={() => onItemClick(item)}
                        className={`w-full text-left px-2 py-1 rounded text-xs truncate ${statusConfig.bgColor} ${statusConfig.textColor} hover:opacity-80`}
                      >
                        {item.title}
                      </button>
                    );
                  })}
                  {weekItems.length > 3 && (
                    <p className="text-xs text-gray-400 px-2">+{weekItems.length - 3} more</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// YEAR PLANNER - WEEKLY LIST VIEW
// ============================================================

const WeeklyListView = ({ items, year, selectedMonth, onItemClick, onUpdateProgress, onMonthChange }) => {
  const weeks = getWeeksInMonth(year, selectedMonth);

  const getItemsForWeek = (weekDays) => {
    const weekStart = weekDays[0];
    const weekEnd = weekDays[6];
    
    return items.filter(item => {
      if (!item.startDate && !item.endDate) return false;
      
      const start = item.startDate ? new Date(item.startDate) : null;
      const end = item.endDate ? new Date(item.endDate) : null;
      
      if (start && end) {
        return start <= weekEnd && end >= weekStart;
      }
      if (start) {
        return start >= weekStart && start <= weekEnd;
      }
      return false;
    });
  };

  return (
    <div className="space-y-4">
      {/* Month Selector */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onMonthChange(selectedMonth === 0 ? 11 : selectedMonth - 1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-lg font-semibold text-gray-900">
          {MONTHS[selectedMonth]} {year}
        </h3>
        <button
          onClick={() => onMonthChange(selectedMonth === 11 ? 0 : selectedMonth + 1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Weeks */}
      <div className="space-y-3">
        {weeks.map((week, weekIndex) => {
          const weekItems = getItemsForWeek(week);
          const weekNum = getWeekNumber(week[0]);
          
          return (
            <div key={weekIndex} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-900">Week {weekNum}</span>
                  <span className="text-sm text-gray-500 ml-2">
                    {formatDateShort(week[0])} - {formatDateShort(week[6])}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{weekItems.length} items</span>
              </div>
              
              {weekItems.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {weekItems.map(item => {
                    const statusConfig = getStatusConfig(item.status);
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <StatusIcon className={`w-4 h-4 text-${statusConfig.color}-500 flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{item.title}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-16">
                            <ProgressBar progress={item.progress || 0} showLabel={false} size="sm" />
                          </div>
                          {item.completionResult && <CompletionBadge result={item.completionResult} />}
                          <button
                            onClick={() => onUpdateProgress(item)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => onItemClick(item)}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center text-gray-400 text-sm">
                  No items this week
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================
// YEAR PLANNER CONTAINER
// ============================================================

const YearPlanner = ({ items, year, onItemClick, onUpdateProgress }) => {
  const [plannerView, setPlannerView] = useState(PlannerView.MONTHLY_LIST);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  return (
    <div className="space-y-4">
      {/* View Toggle */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white rounded-xl border border-gray-100 p-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setPlannerView(PlannerView.MONTHLY_GRID)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              plannerView === PlannerView.MONTHLY_GRID
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <LayoutGrid className="w-4 h-4" /> Monthly Grid
          </button>
          <button
            onClick={() => setPlannerView(PlannerView.MONTHLY_LIST)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              plannerView === PlannerView.MONTHLY_LIST
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <List className="w-4 h-4" /> Monthly List
          </button>
          <button
            onClick={() => setPlannerView(PlannerView.WEEKLY_GRID)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              plannerView === PlannerView.WEEKLY_GRID
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CalendarDays className="w-4 h-4" /> Weekly Grid
          </button>
          <button
            onClick={() => setPlannerView(PlannerView.WEEKLY_LIST)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              plannerView === PlannerView.WEEKLY_LIST
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Rows3 className="w-4 h-4" /> Weekly List
          </button>
        </div>
        <span className="text-sm text-gray-500">{year}</span>
      </div>

      {/* View Content */}
      {plannerView === PlannerView.MONTHLY_GRID && (
        <MonthlyGridView items={items} year={year} onItemClick={onItemClick} />
      )}
      {plannerView === PlannerView.MONTHLY_LIST && (
        <MonthlyListView items={items} year={year} onItemClick={onItemClick} onUpdateProgress={onUpdateProgress} />
      )}
      {plannerView === PlannerView.WEEKLY_GRID && (
        <WeeklyGridView
          items={items}
          year={year}
          selectedMonth={selectedMonth}
          onItemClick={onItemClick}
          onMonthChange={setSelectedMonth}
        />
      )}
      {plannerView === PlannerView.WEEKLY_LIST && (
        <WeeklyListView
          items={items}
          year={year}
          selectedMonth={selectedMonth}
          onItemClick={onItemClick}
          onUpdateProgress={onUpdateProgress}
          onMonthChange={setSelectedMonth}
        />
      )}
    </div>
  );
};

// ============================================================
// PLAN DETAIL VIEW
// ============================================================

const PlanDetail = ({ plan, items, onBack, onEditPlan, onAddItem, onEditItem, onDeleteItem, onUpdateProgress, onReorderItems }) => {
  const [viewMode, setViewMode] = useState(ViewMode.CARDS);
  const [progressItem, setProgressItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const stats = useMemo(() => {
    const total = items.length;
    const completed = items.filter(i => i.status === ItemStatus.COMPLETED).length;
    const inProgress = items.filter(i => i.status === ItemStatus.IN_PROGRESS).length;
    const successful = items.filter(i => i.completionResult === CompletionResult.SUCCESSFUL).length;
    const avgProgress = total > 0 ? Math.round(items.reduce((sum, i) => sum + (i.progress || 0), 0) / total) : 0;
    
    return { total, completed, inProgress, successful, avgProgress };
  }, [items]);

  const handleProgressUpdate = async (itemId, update) => {
    setSaving(true);
    try {
      const item = items.find(i => i.id === itemId);
      await onEditItem({ ...item, ...update });
      setProgressItem(null);
    } catch (error) {
      console.error('Error updating progress:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{plan.name}</h1>
              <p className="text-gray-500">{plan.description || `${plan.year} Operational Plan`}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEditPlan(plan)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-xl flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" /> Edit Plan
            </button>
            <button
              onClick={onAddItem}
              className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add Item
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Total Items</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
            <p className="text-sm text-gray-500">In Progress</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
            <p className="text-sm text-gray-500">Completed</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.successful}</p>
            <p className="text-sm text-gray-500">Successful</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{stats.avgProgress}%</p>
            <p className="text-sm text-gray-500">Avg Progress</p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-100 p-1 w-fit">
          <button
            onClick={() => setViewMode(ViewMode.CARDS)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === ViewMode.CARDS
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Rows3 className="w-4 h-4" /> Cards
          </button>
          <button
            onClick={() => setViewMode(ViewMode.YEAR_PLANNER)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === ViewMode.YEAR_PLANNER
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CalendarRange className="w-4 h-4" /> Year Planner
          </button>
        </div>

        {/* Content */}
        {viewMode === ViewMode.CARDS ? (
          <CardsView
            items={items}
            onEdit={onEditItem}
            onDelete={onDeleteItem}
            onUpdateProgress={setProgressItem}
            onReorder={onReorderItems}
          />
        ) : (
          <YearPlanner
            items={items}
            year={plan.year}
            onItemClick={onEditItem}
            onUpdateProgress={setProgressItem}
          />
        )}

        {/* Progress Update Modal */}
        {progressItem && (
          <ProgressUpdateModal
            item={progressItem}
            onSave={handleProgressUpdate}
            onClose={() => setProgressItem(null)}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
};

// ============================================================
// PLANS LIST
// ============================================================

const PlansList = ({ plans, onSelect, onCreate, onEdit, onDelete }) => {
  if (plans.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="No Operational Plans"
        description="Create your first operational plan to start tracking activities"
        action={
          <button
            onClick={onCreate}
            className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" /> Create Plan
          </button>
        }
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {plans.map(plan => (
        <div
          key={plan.id}
          className={`bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all`}
        >
          <div className={`h-2 bg-${plan.color || 'indigo'}-500`} />
          <div className="p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900 text-lg">{plan.name}</h3>
                <p className="text-sm text-gray-500">{plan.year}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); onEdit(plan); }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); if(window.confirm('Delete plan?')) onDelete(plan.id); }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {plan.description && (
              <p className="text-sm text-gray-500 mb-4 line-clamp-2">{plan.description}</p>
            )}

            {plan.category && (
              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg mb-4">
                {plan.category}
              </span>
            )}

            <button
              onClick={() => onSelect(plan)}
              className={`w-full py-2.5 bg-${plan.color || 'indigo'}-600 text-white rounded-xl hover:bg-${plan.color || 'indigo'}-700 transition-colors`}
            >
              Open Plan
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const OperationalPlannerTool = ({ bookId, customStyling = {}, toolsService, onBack, workspaceId = 'default' }) => {
  // State
  const [plans, setPlans] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [activePlan, setActivePlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const [view, setView] = useState('list'); // 'list', 'plan-form', 'item-form', 'plan-detail'

  // Load data
  useEffect(() => {
    loadData();
  }, [bookId, workspaceId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansData, itemsData] = await Promise.all([
        toolsService.getToolData(bookId, 'operational-plans', workspaceId),
        toolsService.getToolData(bookId, 'operational-items', workspaceId)
      ]);
      setPlans(plansData || []);
      setItems(itemsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get items for active plan
  const planItems = useMemo(() => {
    if (!activePlan) return [];
    return items.filter(item => item.planId === activePlan.id);
  }, [items, activePlan]);

  // Handlers
  const handleSavePlan = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'operational-plans', data, workspaceId);
      await loadData();
      setEditingPlan(null);
      setView('list');
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      // Delete all items in plan
      const planItems = items.filter(i => i.planId === planId);
      for (const item of planItems) {
        await toolsService.deleteToolData(bookId, 'operational-items', item.id, workspaceId);
      }
      await toolsService.deleteToolData(bookId, 'operational-plans', planId, workspaceId);
      await loadData();
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  };

  const handleSaveItem = async (data) => {
    setSaving(true);
    try {
      await toolsService.saveToolData(bookId, 'operational-items', data, workspaceId);
      await loadData();
      setEditingItem(null);
      setView('plan-detail');
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await toolsService.deleteToolData(bookId, 'operational-items', itemId, workspaceId);
      await loadData();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleReorderItems = async (reorderedItems) => {
    // Save order to each item
    for (let i = 0; i < reorderedItems.length; i++) {
      const item = reorderedItems[i];
      if (item.order !== i) {
        await toolsService.saveToolData(bookId, 'operational-items', { ...item, order: i }, workspaceId);
      }
    }
    await loadData();
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }

  // Plan Form
  if (view === 'plan-form') {
    return (
      <PlanForm
        plan={editingPlan}
        onSave={handleSavePlan}
        onCancel={() => { setEditingPlan(null); setView(activePlan ? 'plan-detail' : 'list'); }}
        saving={saving}
      />
    );
  }

  // Item Form
  if (view === 'item-form' && activePlan) {
    return (
      <ItemForm
        item={editingItem}
        planId={activePlan.id}
        onSave={handleSaveItem}
        onCancel={() => { setEditingItem(null); setView('plan-detail'); }}
        saving={saving}
      />
    );
  }

  // Plan Detail
  if (view === 'plan-detail' && activePlan) {
    return (
      <PlanDetail
        plan={activePlan}
        items={planItems}
        onBack={() => { setActivePlan(null); setView('list'); }}
        onEditPlan={(p) => { setEditingPlan(p); setView('plan-form'); }}
        onAddItem={() => { setEditingItem(null); setView('item-form'); }}
        onEditItem={(item) => { setEditingItem(item); setView('item-form'); }}
        onDeleteItem={handleDeleteItem}
        onUpdateProgress={() => {}}
        onReorderItems={handleReorderItems}
      />
    );
  }

  // Plans List (default)
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Operational Planner</h1>
              <p className="text-gray-500">Create and manage operational plans</p>
            </div>
          </div>
          <button
            onClick={() => { setEditingPlan(null); setView('plan-form'); }}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Plan
          </button>
        </div>

        {/* Plans */}
        <PlansList
          plans={plans}
          onSelect={(plan) => { setActivePlan(plan); setView('plan-detail'); }}
          onCreate={() => { setEditingPlan(null); setView('plan-form'); }}
          onEdit={(plan) => { setEditingPlan(plan); setView('plan-form'); }}
          onDelete={handleDeletePlan}
        />
      </div>
    </div>
  );
};

export default OperationalPlannerTool;